import React, { CSSProperties, ReactNode } from "react"
import { useField } from "formik"
import { NumericFormat, type NumberFormatValues } from "react-number-format"
import { FormHelperText, Grid, TextField } from "@mui/material"

import { useNumber } from "../../../../localization/useNumber"

type FormikCurrencyFieldProps = Readonly<{
    name: string
    // Optional: the product form renders its own label above the input, so the field has none of its own
    label?: string
    // Id of that external label, wired up as `aria-labelledby` so the input keeps an accessible name
    labelId?: string
    placeholder?: string | undefined
    errorMessage?: string
    fullWidth?: boolean
    inputTextAlign?: CSSProperties["textAlign"]
    containerDirection?: CSSProperties["flexDirection"]
    gap?: number
    startAdornment?: ReactNode
    disabled?: boolean
    isAllowed?: (values: NumberFormatValues) => boolean
}>

export const FormikCurrencyField = ({
    name,
    label,
    labelId,
    placeholder,
    errorMessage,
    fullWidth = true,
    inputTextAlign = "right",
    containerDirection = "row",
    gap = 1,
    startAdornment,
    disabled,
    isAllowed,
}: FormikCurrencyFieldProps) => {
    const [field, _, helpers] = useField<string>(name)

    return (
        <Grid container gap={gap} flexDirection={containerDirection}>
            <TextField
                name={name}
                label={label}
                placeholder={placeholder}
                value={field.value}
                fullWidth={fullWidth}
                disabled={disabled}
                inputProps={{
                    style: { textAlign: inputTextAlign },
                    isAllowed,
                    "aria-labelledby": labelId,
                }}
                onChange={(e) => {
                    helpers.setValue(e.target.value)
                }}
                InputProps={{
                    inputComponent: NumberFormatCustom as any,
                    startAdornment,
                }}
                error={!!errorMessage}
            />
            {!!errorMessage && <FormHelperText error>{errorMessage}</FormHelperText>}
        </Grid>
    )
}

interface NumberFormatCustomProps {
    inputRef: (instance: HTMLInputElement | null) => void
    onChange: React.FormEventHandler<HTMLInputElement>
    name: string
    isAllowed?: (values: NumberFormatValues) => boolean
}

export const NumberFormatCustom: React.FC<NumberFormatCustomProps> = (props) => {
    const { inputRef, onChange, name, ...other } = props
    const numberFactory = useNumber({})

    return (
        <NumericFormat
            {...other}
            name={name}
            getInputRef={inputRef}
            onValueChange={(values) => {
                onChange({
                    target: {
                        name: name,
                        value: values.value,
                    },
                } as React.ChangeEvent<HTMLInputElement>)
            }}
            thousandSeparator={numberFactory.groupSeparator}
            decimalSeparator={numberFactory.decimalSeparator}
            decimalScale={2}
            prefix=""
        />
    )
}
